import { processAPIResponse } from '@/utils/request';
import {
  createLeadTaskRequest,
  deleteLeadTaskRequest,
  fetchLeadTasksList,
  fetchSingleLeadTaskRequest,
} from '@/services/api/leadTask';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import { FILTER_PLACES } from '@/components/Filters/LeadFilters';
import { parseSort } from '@/utils/functions';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { STATUS_FILTER_VALUES } from '@/pages/Tasks/constants';
import omit from 'lodash/omit';

// The API's maximum; an appointment rarely carries more than a handful of tasks.
const APPOINTMENT_TASKS_PAGE_SIZE = 500;

const initialState = {
  list: [],
  links: {},
  // Which filter set `links` belongs to — the Tasks page refuses to page while
  // this disagrees with the filters on screen. See the `save` reducer.
  linksFiltersKey: '',
  meta: {},
  overviewList: [],
  createTaskDrawerOpen: false,
  createTaskDrawerTask: null,
  createTaskDrawerLeadId: null,
  // Details drawer: open = Boolean(taskId); toggledAt feeds Drawer's z-index
  // stacking (same shape as leads/toggleLeadDetailsDrawer)
  taskDetailsDrawer: { taskId: null, toggledAt: null },
  // Single tasks keyed by id — richer than list rows (full lead via
  // related_item, action consequences); refreshed independently of the list
  taskDetails: {},
  // open_add_comment UI consequence: text to pre-fill the drawer's comment
  // composer with; `at` re-triggers when the same text arrives twice
  commentPrefill: null,
  // Tasks linked to one appointment (related_item = serviceRequest), keyed by its id
  appointmentTasks: {},
};
export default {
  namespace: 'leadTasks',

  state: initialState,

  effects: {
    fetch: [
      function*({ payload = {} }: { payload: { search?: string, url?: string } }, { call, put, select }) {
        const filters = yield select(state => PreferencesSelectors.selectFilters(state, FILTER_PLACES.TASKS_TABLE));
        const { statusFilter = STATUS_FILTER_VALUES.OPEN } = filters;

        const query = { ...filters };
        delete query.statusFilter;

        if (!isEmpty(query.date)) {
          // const dateFilterType = preferences?.dateFilterType?.value || 'due'
          const dateFilterType = 'due';
          query[`${dateFilterType}_from`] = dayjs(query.date?.start).format('YYYY-MM-DD');
          query[`${dateFilterType}_to`] = dayjs(query.date?.end).format('YYYY-MM-DD');
        }
        delete query.date;

        // There's a default sort
        if (!query.sort)
          query.sort = '-due_date';

        const { sort_by, sort_direction } = parseSort(query.sort);
        if (sort_by && sort_direction) {
          delete query.sort;
          query.sort_by = sort_by;
          query.sort_direction = sort_direction;
        }

        switch (statusFilter) {
          case STATUS_FILTER_VALUES.OPEN:
            // Snoozed tasks have their own tab — Open means "needs attention now"
            query.is_open = 1;
            query.is_snoozed = 0;
            break;
          case STATUS_FILTER_VALUES.SNOOZED:
            query.is_open = 1;
            query.is_snoozed = 1;
            break;
          case STATUS_FILTER_VALUES.CLOSED:
            query.is_closed = 1;
            break;

        }
        if (query.group_by) {
          query.groups_to_be_loaded = 'all';
        }

        const { response, data } = yield call(fetchLeadTasksList, payload?.url ? { url: payload.url } : { query });

        yield processAPIResponse({ response, data }, {
          * onSuccess(payloadData) {
            yield put({
              type: 'save',
              payload: payloadData,
              options: {
                concat: Boolean(payload?.url),
                filtersKey: JSON.stringify(filters),
              },
            });
          },
        });
      }, {
        type: 'takeLatest',
      }
    ],
    * fetchOverview({ payload = {} }: { payload: { assigneeId?: string } }, { call, put }) {
      // is_snoozed: 0 — snoozed tasks are deliberately out of the "needs my
      // attention now" overview until their snooze expires
      const { response, data } = yield call(fetchLeadTasksList, { query: { assignee_id: payload.assigneeId, is_open: 1, is_snoozed: 0 } });
      yield processAPIResponse({ response, data }, {
        * onSuccess(payloadData) {
          const list = Array.isArray(payloadData) ? payloadData : (payloadData?.data || []);
          yield put({
            type: 'saveOverview',
            payload: list,
          });
        },
      });
    },
    * fetchAppointmentTasks({ payload }: { payload: { appointmentId?: string } }, { call, put }) {
      const { appointmentId } = payload || {};
      if (!appointmentId) return;
      const { response, data } = yield call(fetchLeadTasksList, {
        query: { service_request_id: appointmentId, page_size: APPOINTMENT_TASKS_PAGE_SIZE },
      });
      yield processAPIResponse({ response, data }, {
        * onSuccess(tasks) {
          yield put({ type: 'saveAppointmentTasks', payload: { appointmentId, tasks: tasks || [] } });
        },
        * onError() {},
      }, 'data');
    },
    * fetchSingleTask({ payload = {} }: { payload: { taskId?: string } }, { call, put }) {
      const { taskId } = payload;
      if (!taskId) return;
      const { response, data } = yield call(fetchSingleLeadTaskRequest, { params: { taskId } });
      if (response?.status === 404) {
        // Deleted (from the drawer's menu or elsewhere) — nothing left to show
        yield put({ type: 'closeTaskDetailsDrawer' });
        return;
      }
      yield processAPIResponse({ response, data }, {
        * onSuccess(task) {
          yield put({ type: 'saveTaskDetails', payload: task });
          // Seed the embedded lead into the leads store so lead-driven UI
          // (name tags, drawers) can render before the full details fetch lands
          const lead = task?.related_item?.type === 'lead'
            ? task.related_item.data
            : (task?.owners || []).find((owner: any) => owner?.type === 'lead')?.data;
          if (lead?.id) {
            yield put({ type: 'leads/saveLeadsData', payload: { list: [lead] } });
          }
        },
      }, 'data');
    },
    * create({ payload = {} }: { payload: { data?: any, then?: () => void } }, { call, put, select }) {
      const { response, data } = yield call(createLeadTaskRequest, { data: payload.data });
      yield processAPIResponse({ response, data }, {
        * onSuccess(data) {
          yield put({
            type: 'fetch',
          });
          payload.then && payload.then();
        },
      }, 'data');
    },
    * deleteTask({ payload: { taskId, cb, intl } }, { call, put }) {
      const { response, data } = yield call(deleteLeadTaskRequest, { params: { taskId } });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess() {

            yield put({
              type: 'fetch',
            });
            cb && cb(data);
          },
          * onError(error) {},
        },
      );
    },

  },

  reducers: {
    saveAppointmentTasks(state, action) {
      const { appointmentId, tasks } = action.payload;
      return { ...state, appointmentTasks: { ...state.appointmentTasks, [appointmentId]: tasks } };
    },
    openTaskDetailsDrawer(state, action) {
      const task = action.payload?.task || null;
      const taskId = task?.id || action.payload?.taskId || null;
      return {
        ...state,
        taskDetailsDrawer: { taskId, toggledAt: new Date().getTime() },
        // A full row passed along renders instantly; the fetch then upgrades it
        taskDetails: task ? { ...state.taskDetails, [task.id]: task } : state.taskDetails,
      };
    },
    closeTaskDetailsDrawer(state) {
      return {
        ...state,
        taskDetailsDrawer: { taskId: null, toggledAt: null },
        commentPrefill: null,
      };
    },
    setCommentPrefill(state, action) {
      return {
        ...state,
        commentPrefill: action.payload || null,
      };
    },
    saveTaskDetails(state, action) {
      const task = action.payload;
      if (!task?.id) return state;
      return {
        ...state,
        taskDetails: { ...state.taskDetails, [task.id]: task },
      };
    },
    openCreateTaskDrawer(state, action) {
      return {
        ...state,
        createTaskDrawerOpen: true,
        createTaskDrawerTask: action.payload?.task || null,
        createTaskDrawerLeadId: action.payload?.leadId || null,
        onTaskCreated: action.payload?.onTaskCreated || null,
        createTaskDrawerOptions: omit(action?.payload, ['task', 'leadId', 'onTaskCreated']) || {}
      };
    },
    closeCreateTaskDrawer(state) {
      return {
        ...state,
        createTaskDrawerOpen: false,
        createTaskDrawerTask: null,
        createTaskDrawerLeadId: null,
        onTaskCreated: null,
      };
    },
    save(state, action) {
      const payload = action.payload || {};
      const list = Array.isArray(payload) ? payload : (payload.data || []);
      const links = Array.isArray(payload) ? (state.links || {}) : (payload.links || {});
      const meta = Array.isArray(payload) ? (state.meta || {}) : (payload.meta || {});
      // Stamped so a consumer can tell whether `links` still describe the
      // filters it is showing: `fetch` is takeLatest, so a next-page request
      // built from links of a superseded filter set would cancel the page-1
      // fetch that replaced them and concat its rows onto the wrong list.
      const linksFiltersKey = action.options?.filtersKey ?? state.linksFiltersKey;
      if (action.options?.concat) {
        return {
          ...state,
          list: (state.list || []).concat(list),
          links,
          linksFiltersKey,
          meta,
        };
      }
      return {
        ...state,
        list: list,
        links,
        linksFiltersKey,
        meta,
      };
    },
    saveOverview(state, action) {
      return {
        ...state,
        overviewList: action.payload || [],
      };
    },
    reset() {
      return initialState;
    },
  },
};
